/**
 * Workflow Engine — Main UI Component
 * Modular, self-contained workflow management system.
 * Tabs: Cockpit | Form Builder | Workflow Designer | Instances
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiFetch } from '../../../client/bizsuite-app';

/* ═══════════════════════════════════════════════════════════
   NEON ICON LIBRARY (monochrome, 16px SVG)
   ═══════════════════════════════════════════════════════════ */
const IC = (d, color) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color || 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{d}</svg>;

const Icons = {
  dashboard: (c) => IC(<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>, c),
  form: (c) => IC(<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>, c),
  workflow: (c) => IC(<><circle cx="12" cy="5" r="3"/><line x1="12" y1="8" x2="12" y2="14"/><circle cx="6" cy="19" r="3"/><circle cx="18" cy="19" r="3"/><line x1="12" y1="14" x2="6" y2="16"/><line x1="12" y1="14" x2="18" y2="16"/></>, c),
  instances: (c) => IC(<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>, c),
  plus: (c) => IC(<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>, c),
  trash: (c) => IC(<><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></>, c),
  edit: (c) => IC(<><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>, c),
  check: (c) => IC(<><polyline points="20 6 9 17 4 12"/></>, c),
  x: (c) => IC(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>, c),
  play: (c) => IC(<><polygon points="5 3 19 12 5 21 5 3"/></>, c),
  pause: (c) => IC(<><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></>, c),
  table: (c) => IC(<><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></>, c),
  flag: (c) => IC(<><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>, c),
  clock: (c) => IC(<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>, c),
  bolt: (c) => IC(<><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>, c),
  grip: (c) => IC(<><circle cx="9" cy="6" r="1.5" fill={c||'currentColor'} stroke="none"/><circle cx="15" cy="6" r="1.5" fill={c||'currentColor'} stroke="none"/><circle cx="9" cy="12" r="1.5" fill={c||'currentColor'} stroke="none"/><circle cx="15" cy="12" r="1.5" fill={c||'currentColor'} stroke="none"/><circle cx="9" cy="18" r="1.5" fill={c||'currentColor'} stroke="none"/><circle cx="15" cy="18" r="1.5" fill={c||'currentColor'} stroke="none"/></>, c),
  forward: (c) => IC(<><polyline points="15 17 20 12 15 7"/><path d="M4 18v-2a4 4 0 014-4h12"/></>, c),
  reply: (c) => IC(<><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 00-4-4H4"/></>, c),
  save: (c) => IC(<><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></>, c),
  eye: (c) => IC(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>, c),
  chevDown: (c) => IC(<><polyline points="6 9 12 15 18 9"/></>, c),
  arrowRight: (c) => IC(<><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>, c),
  columns: (c) => IC(<><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></>, c),
};

/* ═══════════════════════════════════════════════════════════
   NEON COLORS
   ═══════════════════════════════════════════════════════════ */
const N = {
  cyan: '#00F0FF', magenta: '#FF00E5', lime: '#39FF14', orange: '#FF6B00',
  violet: '#BF5FFF', pink: '#FF2D7B', teal: '#00E5A0', blue: '#3B8FF4',
  yellow: '#FFE500', coral: '#FF4F4F', mint: '#00FFC8', gold: '#FFB300',
};

/* ═══════════════════════════════════════════════════════════
   SHARED STYLES
   ═══════════════════════════════════════════════════════════ */
const S = {
  card: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 20 },
  cardSm: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14 },
  input: { width: '100%', padding: '8px 12px', fontSize: 13, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#E2E8F0', outline: 'none' },
  select: { padding: '8px 12px', fontSize: 13, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#E2E8F0', outline: 'none', cursor: 'pointer' },
  label: { fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' },
  neonBtn: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px', fontSize: 12.5, fontWeight: 600,
    borderRadius: 8, border: `1px solid ${color}33`, background: `${color}0A`, color: color,
    cursor: 'pointer', transition: 'all 0.2s', outline: 'none', whiteSpace: 'nowrap',
  }),
  ghost: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12.5, fontWeight: 500, borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.03)', color: '#CBD5E1', cursor: 'pointer', transition: 'all 0.2s', outline: 'none' },
  badge: (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: `${color}18`, color }),
  stepCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px', cursor: 'grab' },
  heading: { fontSize: 15, fontWeight: 700, color: '#E2E8F0', marginBottom: 4 },
  muted: { fontSize: 12.5, color: '#64748B' },
  empty: { padding: 40, textAlign: 'center', color: '#475569', fontSize: 13 },
  statCard: (color) => ({
    ...S.cardSm, display: 'flex', alignItems: 'center', gap: 14, flex: 1,
    borderLeft: `3px solid ${color}`,
  }),
};
// Fix: S.statCard references S before it's fully defined; make it a fn
const statCard = (color) => ({
  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 14,
  display: 'flex', alignItems: 'center', gap: 14, flex: 1, borderLeft: `3px solid ${color}`,
});

/* ═══════════════════════════════════════════════════════════
   API HELPER
   ═══════════════════════════════════════════════════════════ */
const wfApi = async (path, opts = {}) => {
  try {
    const res = await apiFetch(`/api/workflow${path}`, opts);
    if (!res.ok) {
      const text = await res.text();
      console.error(`[WF] ${path} failed (${res.status}):`, text);
      return {};
    }
    return res.json();
  } catch (e) {
    console.error(`[WF] ${path} error:`, e.message);
    return {};
  }
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
const WF_TABS = [
  { key: 'cockpit', label: 'Cockpit', icon: Icons.dashboard, color: N.cyan },
  { key: 'forms', label: 'Form Builder', icon: Icons.form, color: N.lime },
  { key: 'designer', label: 'Designer', icon: Icons.workflow, color: N.violet },
  { key: 'instances', label: 'Instances', icon: Icons.instances, color: N.orange },
];

function tabFromHash() {
  const h = window.location.hash.replace('#', '');
  if (h === '/workflow/forms') return 'forms';
  if (h === '/workflow/designer') return 'designer';
  if (h === '/workflow/instances') return 'instances';
  return 'cockpit';
}

export default function WorkflowEngine() {
  const [tab, setTab] = useState(tabFromHash);
  const [flash, setFlash] = useState(null);

  function showFlash(text, type = 'success') {
    setFlash({ text, type });
    setTimeout(() => setFlash(null), 3000);
  }

  return (
    <div>
      {flash && <div className={`flash-msg ${flash.type}`}>{flash.text}</div>}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'rgba(255,255,255,0.02)', borderRadius: 12, padding: 4, border: '1px solid rgba(255,255,255,0.05)' }}>
        {WF_TABS.map(t => (
          <button key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '10px 16px', fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
              border: 'none', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s',
              background: tab === t.key ? `${t.color}15` : 'transparent',
              color: tab === t.key ? t.color : '#94A3B8',
            }}>
            {t.icon(tab === t.key ? t.color : '#64748B')}
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'cockpit' && <CockpitTab flash={showFlash} setTab={setTab} />}
      {tab === 'forms' && <FormBuilderTab flash={showFlash} />}
      {tab === 'designer' && <DesignerTab flash={showFlash} />}
      {tab === 'instances' && <InstancesTab flash={showFlash} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   COCKPIT TAB
   ═══════════════════════════════════════════════════════════ */
function CockpitTab({ flash, setTab }) {
  const [stats, setStats] = useState({ forms: 0, workflows: 0, pending: 0, completed: 0 });
  const [activity, setActivity] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [tileEdit, setTileEdit] = useState(null);
  const [loading, setLoading] = useState(true);

  const MENU_PAGES = [
    { value: 'self-service', label: 'Self Service (ESS)' },
    { value: 'leave', label: 'Leave' },
    { value: 'recruitment', label: 'Recruitment' },
    { value: 'projects', label: 'Projects' },
    { value: 'finance', label: 'Finance' },
    { value: 'procurement', label: 'Procurement' },
    { value: 'dashboard', label: 'Dashboard' },
  ];

  useEffect(() => {
    Promise.all([
      wfApi('/cockpit/stats'),
      wfApi('/cockpit/activity'),
      wfApi('/cockpit/triggers'),
      wfApi('/tiles'),
      wfApi('/workflows'),
    ]).then(([s, a, t, ti, w]) => {
      setStats(s);
      setActivity(a.activity || []);
      setTriggers(t.triggers || []);
      setTiles(ti.tiles || []);
      setWorkflows(w.workflows || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={S.empty}><div className="spinner" style={{ margin: '0 auto 12px' }} />Loading cockpit...</div>;

  const actionColors = { initiated: N.blue, approve: N.lime, reject: N.coral, forward: N.orange, resend: N.yellow, cancel: '#94A3B8' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12 }}>
        {[
          { label: 'Forms', value: stats.forms, color: N.lime, icon: Icons.form },
          { label: 'Workflows', value: stats.workflows, color: N.violet, icon: Icons.workflow },
          { label: 'Pending', value: stats.pending, color: N.orange, icon: Icons.clock },
          { label: 'Completed', value: stats.completed, color: N.teal, icon: Icons.check },
        ].map((s, i) => (
          <div key={i} style={statCard(s.color)}>
            <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}12` }}>
              {s.icon(s.color)}
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#E2E8F0' }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 14 }}>
        {/* Quick actions */}
        <div style={{ ...S.card, flex: 1 }}>
          <div style={S.heading}>Quick Actions</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button style={S.neonBtn(N.lime)} onClick={() => setTab('forms')}>{Icons.plus(N.lime)} New Form</button>
            <button style={S.neonBtn(N.violet)} onClick={() => setTab('designer')}>{Icons.plus(N.violet)} New Workflow</button>
          </div>
        </div>

        {/* Active triggers */}
        <div style={{ ...S.card, flex: 1 }}>
          <div style={S.heading}>Active Triggers</div>
          <div style={{ marginTop: 8 }}>
            {triggers.length === 0 && <div style={S.muted}>No triggers configured yet</div>}
            {triggers.slice(0, 5).map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                {t.trigger_type === 'schedule' ? Icons.clock(N.yellow) : Icons.bolt(N.orange)}
                <span style={{ fontSize: 12.5, color: '#CBD5E1', flex: 1 }}>{t.workflow_name || 'Workflow'}</span>
                <span style={S.badge(t.enabled ? N.teal : '#64748B')}>{t.enabled ? 'Active' : 'Paused'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div style={S.card}>
        <div style={S.heading}>Recent Activity</div>
        <div style={{ marginTop: 10 }}>
          {activity.length === 0 && <div style={S.muted}>No workflow activity yet. Create a form and workflow to get started.</div>}
          {activity.slice(0, 10).map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ ...S.badge(actionColors[a.action] || '#94A3B8'), minWidth: 70, textAlign: 'center' }}>{a.action}</span>
              <span style={{ fontSize: 12.5, color: '#CBD5E1', flex: 1 }}>{a.actor_name || 'System'} — {a.workflow_name || 'Workflow'}</span>
              <span style={{ fontSize: 11, color: '#64748B' }}>{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Menu Tiles Management */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={S.heading}>Menu Tiles</div>
          <button style={S.neonBtn(N.cyan)} onClick={() => setTileEdit({
            workflow_id: '', menu_page: 'self-service', title: '', subtitle: '', icon: 'play-circle', color: '#00e0ff', sequence: 0, status: 'Active',
          })}>{Icons.plus(N.cyan)} New Tile</button>
        </div>
        <div style={{ fontSize: 12, color: '#64748B', marginBottom: 10 }}>
          Place workflow shortcuts on Self Service and other menu pages.
        </div>
        {tiles.length === 0 ? (
          <div style={S.muted}>No tiles configured. Add a tile to place a workflow shortcut on a menu page.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {tiles.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: t.color || '#00e0ff' }} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#E2E8F0' }}>{t.title}</span>
                  <span style={{ fontSize: 11, color: '#64748B', marginLeft: 8 }}>→ {t.workflow_name || 'Workflow'}</span>
                </div>
                <span style={S.badge(N.cyan)}>{MENU_PAGES.find(p => p.value === t.menu_page)?.label || t.menu_page}</span>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}
                  onClick={() => setTileEdit({ ...t })}>{Icons.edit()}</button>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F87171', padding: 4 }}
                  onClick={async () => {
                    await wfApi(`/tiles/${t.id}`, { method: 'DELETE' });
                    setTiles(prev => prev.filter(x => x.id !== t.id));
                    flash('Tile removed');
                  }}>{Icons.trash('#F87171')}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tile Edit Modal */}
      {tileEdit && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => e.target === e.currentTarget && setTileEdit(null)}>
          <div style={{ ...S.card, width: 480, maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ ...S.heading, marginBottom: 14 }}>{tileEdit.id ? 'Edit Tile' : 'New Tile'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={S.label}>Workflow *</label>
                <select style={{ ...S.select, width: '100%' }} value={tileEdit.workflow_id}
                  onChange={e => {
                    const wf = workflows.find(w => w.id === e.target.value);
                    setTileEdit(p => ({ ...p, workflow_id: e.target.value, title: p.title || wf?.name || '' }));
                  }}>
                  <option value="">-- Select --</option>
                  {workflows.filter(w => w.status === 'Active').map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Menu Page *</label>
                <select style={{ ...S.select, width: '100%' }} value={tileEdit.menu_page}
                  onChange={e => setTileEdit(p => ({ ...p, menu_page: e.target.value }))}>
                  {MENU_PAGES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Title *</label>
                  <input style={S.input} value={tileEdit.title} placeholder="Tile title"
                    onChange={e => setTileEdit(p => ({ ...p, title: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Subtitle</label>
                  <input style={S.input} value={tileEdit.subtitle} placeholder="Optional subtitle"
                    onChange={e => setTileEdit(p => ({ ...p, subtitle: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Color</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['#00e0ff', '#39FF14', '#FF00E5', '#FF6B00', '#BF5FFF', '#FFE500'].map(c => (
                      <div key={c} onClick={() => setTileEdit(p => ({ ...p, color: c }))}
                        style={{ width: 24, height: 24, borderRadius: 6, background: c, cursor: 'pointer',
                          border: tileEdit.color === c ? '2px solid white' : '2px solid transparent' }} />
                    ))}
                  </div>
                </div>
                <div style={{ width: 80 }}>
                  <label style={S.label}>Order</label>
                  <input type="number" style={S.input} value={tileEdit.sequence}
                    onChange={e => setTileEdit(p => ({ ...p, sequence: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={S.ghost} onClick={() => setTileEdit(null)}>Cancel</button>
              <button style={S.neonBtn(N.cyan)} onClick={async () => {
                if (!tileEdit.workflow_id || !tileEdit.title) { flash('Workflow and title are required', 'error'); return; }
                const res = await wfApi('/tiles', { method: 'POST', body: JSON.stringify(tileEdit) });
                if (res.success) {
                  flash('Tile saved');
                  const updated = await wfApi('/tiles');
                  setTiles(updated.tiles || []);
                  setTileEdit(null);
                }
              }}>{Icons.save(N.cyan)} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FORM BUILDER TAB
   ═══════════════════════════════════════════════════════════ */
function FormBuilderTab({ flash }) {
  const [forms, setForms] = useState([]);
  const [tables, setTables] = useState([]);
  const [editing, setEditing] = useState(null); // form object being edited
  const [loading, setLoading] = useState(true);

  const loadForms = useCallback(async () => {
    const data = await wfApi('/forms');
    setForms(data.forms || []);
  }, []);

  const loadTables = useCallback(async () => {
    const data = await wfApi('/schema/tables');
    setTables(data.tables || []);
  }, []);

  useEffect(() => {
    Promise.all([loadForms(), loadTables()]).finally(() => setLoading(false));
  }, []);

  function startNew() {
    setEditing({ name: '', description: '', source_table: '', fields: [], custom_sections: [] });
  }

  function editForm(f) {
    setEditing({ ...f });
  }

  async function deleteForm(id) {
    await wfApi(`/forms/${id}`, { method: 'DELETE' });
    flash('Form deleted');
    loadForms();
  }

  async function saveForm() {
    if (!editing.name || !editing.source_table) { flash('Name and table are required', 'error'); return; }
    await wfApi('/forms', { method: 'POST', body: JSON.stringify(editing) });
    flash('Form saved');
    setEditing(null);
    loadForms();
  }

  if (loading) return <div style={S.empty}><div className="spinner" style={{ margin: '0 auto 12px' }} />Loading...</div>;

  // ── Editing view ──
  if (editing) {
    const selectedTable = tables.find(t => t.table === editing.source_table);
    const availableCols = selectedTable?.columns?.filter(c => !c.pk && c.name !== 'tenant_id') || [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={S.heading}>{editing.id ? 'Edit Form' : 'New Form'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.ghost} onClick={() => setEditing(null)}>{Icons.x()} Cancel</button>
            <button style={S.neonBtn(N.lime)} onClick={saveForm}>{Icons.save(N.lime)} Save Form</button>
          </div>
        </div>

        <div style={{ ...S.card, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Form Name *</label>
              <input style={S.input} value={editing.name} placeholder="e.g. Employee Onboarding Form"
                onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Description</label>
              <input style={S.input} value={editing.description} placeholder="Brief description"
                onChange={e => setEditing(p => ({ ...p, description: e.target.value }))} />
            </div>
          </div>

          <div>
            <label style={S.label}>Source Table *</label>
            <select style={{ ...S.select, width: '100%' }} value={editing.source_table}
              onChange={e => setEditing(p => ({ ...p, source_table: e.target.value, fields: [] }))}>
              <option value="">-- Select a table --</option>
              {tables.map(t => (
                <option key={t.table} value={t.table}>{t.table} ({t.rowCount ?? '?'} rows, {t.columns.length} cols)</option>
              ))}
            </select>
          </div>
        </div>

        {/* Field selection */}
        {editing.source_table && (
          <div style={S.card}>
            <div style={{ ...S.heading, marginBottom: 12 }}>Select Fields</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {availableCols.map(col => {
                const isSelected = editing.fields.some(f => f.name === col.name);
                return (
                  <button key={col.name}
                    onClick={() => {
                      setEditing(p => ({
                        ...p,
                        fields: isSelected
                          ? p.fields.filter(f => f.name !== col.name)
                          : [...p.fields, { name: col.name, type: col.type, label: col.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), required: false, editable: true }]
                      }));
                    }}
                    style={{
                      padding: '6px 14px', fontSize: 12, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${isSelected ? N.lime + '55' : 'rgba(255,255,255,0.1)'}`,
                      background: isSelected ? `${N.lime}12` : 'rgba(255,255,255,0.03)',
                      color: isSelected ? N.lime : '#94A3B8', transition: 'all 0.2s',
                    }}>
                    {isSelected ? Icons.check(N.lime) : null} {col.name}
                    <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.6 }}>{col.type}</span>
                  </button>
                );
              })}
            </div>
            {editing.fields.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#94A3B8' }}>{editing.fields.length} field(s) selected</div>
            )}
          </div>
        )}

        {/* Selected fields config */}
        {editing.fields.length > 0 && (
          <div style={S.card}>
            <div style={{ ...S.heading, marginBottom: 12 }}>Configure Fields</div>
            {editing.fields.map((f, idx) => (
              <div key={f.name} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ color: '#64748B', fontSize: 11, width: 24 }}>{idx + 1}</span>
                <input style={{ ...S.input, width: 200 }} value={f.label}
                  onChange={e => {
                    const updated = [...editing.fields];
                    updated[idx] = { ...updated[idx], label: e.target.value };
                    setEditing(p => ({ ...p, fields: updated }));
                  }} />
                <span style={{ fontSize: 11, color: '#64748B', width: 60 }}>{f.type}</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#94A3B8', cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.required}
                    onChange={e => {
                      const updated = [...editing.fields];
                      updated[idx] = { ...updated[idx], required: e.target.checked };
                      setEditing(p => ({ ...p, fields: updated }));
                    }} />
                  Required
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#94A3B8', cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.editable !== false}
                    onChange={e => {
                      const updated = [...editing.fields];
                      updated[idx] = { ...updated[idx], editable: e.target.checked };
                      setEditing(p => ({ ...p, fields: updated }));
                    }} />
                  Editable
                </label>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F87171', padding: 4 }}
                  onClick={() => setEditing(p => ({ ...p, fields: p.fields.filter((_, i) => i !== idx) }))}>{Icons.trash('#F87171')}</button>
              </div>
            ))}
          </div>
        )}

        {/* Flexible Fields */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={S.heading}>Flexible Fields (Date / Days / Time)</div>
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 10 }}>
            Toggle fields to include them in this form. Labels can be overridden per form (and again per workflow).
          </div>
          {[
            { group: 'Date Fields', prefix: 'flex_date_', type: 'date', count: 5 },
            { group: 'Days Fields', prefix: 'flex_days_', type: 'number', count: 5 },
            { group: 'Time Fields', prefix: 'flex_time_', type: 'time', count: 5 },
          ].map(g => (
            <div key={g.group} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 6 }}>{g.group}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Array.from({ length: g.count }, (_, i) => {
                  const key = `${g.prefix}${i + 1}`;
                  const existing = (editing.fields || []).find(f => f.name === key);
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button
                        onClick={() => {
                          setEditing(p => ({
                            ...p,
                            fields: existing
                              ? p.fields.filter(f => f.name !== key)
                              : [...p.fields, { name: key, type: g.type, label: `${g.group.split(' ')[0]} ${i + 1}`, required: false, editable: true, flex: true }]
                          }));
                        }}
                        style={{
                          padding: '5px 12px', fontSize: 11.5, borderRadius: 6, cursor: 'pointer',
                          border: `1px solid ${existing ? N.cyan + '55' : 'rgba(255,255,255,0.1)'}`,
                          background: existing ? `${N.cyan}12` : 'rgba(255,255,255,0.03)',
                          color: existing ? N.cyan : '#64748B', transition: 'all 0.2s',
                        }}>
                        {existing ? Icons.check(N.cyan) : null} {existing?.label || `${g.group.split(' ')[0]} ${i + 1}`}
                      </button>
                      {existing && (
                        <input style={{ ...S.input, width: 120, padding: '4px 8px', fontSize: 11 }}
                          value={existing.label} placeholder="Label"
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            const updated = editing.fields.map(f => f.name === key ? { ...f, label: e.target.value } : f);
                            setEditing(p => ({ ...p, fields: updated }));
                          }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Custom text sections */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={S.heading}>Custom Text Sections</div>
            <button style={S.ghost} onClick={() => setEditing(p => ({
              ...p, custom_sections: [...p.custom_sections, { id: Date.now(), title: '', content: '', position: 'before' }]
            }))}>{Icons.plus()} Add Section</button>
          </div>
          {editing.custom_sections.length === 0 && <div style={S.muted}>No custom sections added</div>}
          {editing.custom_sections.map((sec, idx) => (
            <div key={sec.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <input style={{ ...S.input, width: 180 }} value={sec.title} placeholder="Section title"
                onChange={e => {
                  const updated = [...editing.custom_sections];
                  updated[idx] = { ...updated[idx], title: e.target.value };
                  setEditing(p => ({ ...p, custom_sections: updated }));
                }} />
              <textarea style={{ ...S.input, flex: 1, minHeight: 40, resize: 'vertical' }} value={sec.content} placeholder="Custom text / instructions..."
                onChange={e => {
                  const updated = [...editing.custom_sections];
                  updated[idx] = { ...updated[idx], content: e.target.value };
                  setEditing(p => ({ ...p, custom_sections: updated }));
                }} />
              <select style={S.select} value={sec.position || 'before'}
                onChange={e => {
                  const updated = [...editing.custom_sections];
                  updated[idx] = { ...updated[idx], position: e.target.value };
                  setEditing(p => ({ ...p, custom_sections: updated }));
                }}>
                <option value="before">Before fields</option>
                <option value="after">After fields</option>
              </select>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F87171', padding: 4, marginTop: 4 }}
                onClick={() => setEditing(p => ({ ...p, custom_sections: p.custom_sections.filter((_, i) => i !== idx) }))}>{Icons.trash('#F87171')}</button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── List view ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={S.heading}>Form Templates</div>
        <button style={S.neonBtn(N.lime)} onClick={startNew}>{Icons.plus(N.lime)} New Form</button>
      </div>

      {forms.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{Icons.form('#475569')}</div>
          No forms yet. Click "New Form" to create your first form template.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {forms.map(f => (
            <div key={f.id} style={{ ...S.cardSm, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${N.lime}12` }}>
                {Icons.form(N.lime)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: '#E2E8F0' }}>{f.name}</div>
                <div style={{ fontSize: 11.5, color: '#64748B' }}>{f.source_table} — {f.fields?.length || 0} fields</div>
              </div>
              <span style={S.badge(f.status === 'Active' ? N.teal : '#64748B')}>{f.status}</span>
              <button style={S.ghost} onClick={() => editForm(f)}>{Icons.edit()}</button>
              <button style={{ ...S.ghost, color: '#F87171', borderColor: 'rgba(248,113,113,0.2)' }} onClick={() => deleteForm(f.id)}>{Icons.trash('#F87171')}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   WORKFLOW DESIGNER TAB (Hybrid: sequential + visual)
   ═══════════════════════════════════════════════════════════ */
function DesignerTab({ flash }) {
  const [workflows, setWorkflows] = useState([]);
  const [forms, setForms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState('sequential'); // sequential | graph
  const [loading, setLoading] = useState(true);
  const [dragIdx, setDragIdx] = useState(null);
  // Simulation state
  const [simWf, setSimWf] = useState(null);       // workflow being simulated
  const [simForm, setSimForm] = useState(null);    // linked form definition
  const [simStep, setSimStep] = useState(-1);      // -1 = fill form, 0..n = step index, 999 = done
  const [simFormValues, setSimFormValues] = useState({});
  const [simLog, setSimLog] = useState([]);        // action log
  const [simComment, setSimComment] = useState('');
  const [simForwardTo, setSimForwardTo] = useState('');

  const loadAll = useCallback(async () => {
    const [wData, fData] = await Promise.all([wfApi('/workflows'), wfApi('/forms')]);
    setWorkflows(wData.workflows || []);
    setForms(fData.forms || []);
  }, []);

  useEffect(() => { loadAll().finally(() => setLoading(false)); }, []);

  function startNew() {
    setEditing({
      name: '', description: '', form_id: '', trigger_type: 'manual', trigger_config: {},
      steps: [{ id: 's_' + Date.now(), name: 'Approval', type: 'approval', recipient_type: 'user', recipient_id: '', recipient_name: '', conditions: [], custom_text: '' }],
      canvas_data: {},
    });
  }

  function addStep() {
    setEditing(p => ({
      ...p,
      steps: [...p.steps, {
        id: 's_' + Date.now(), name: `Step ${p.steps.length + 1}`, type: 'approval',
        recipient_type: 'user', recipient_id: '', recipient_name: '', conditions: [], custom_text: '',
      }]
    }));
  }

  function updateStep(idx, key, value) {
    setEditing(p => {
      const steps = [...p.steps];
      steps[idx] = { ...steps[idx], [key]: value };
      return { ...p, steps };
    });
  }

  function removeStep(idx) {
    setEditing(p => ({ ...p, steps: p.steps.filter((_, i) => i !== idx) }));
  }

  function addCondition(stepIdx) {
    setEditing(p => {
      const steps = [...p.steps];
      steps[stepIdx] = {
        ...steps[stepIdx],
        conditions: [...(steps[stepIdx].conditions || []),
          { id: 'c_' + Date.now(), field: '', operator: '==', value: '', logic: 'AND' }]
      };
      return { ...p, steps };
    });
  }

  function updateCondition(stepIdx, condIdx, key, value) {
    setEditing(p => {
      const steps = [...p.steps];
      const conds = [...steps[stepIdx].conditions];
      conds[condIdx] = { ...conds[condIdx], [key]: value };
      steps[stepIdx] = { ...steps[stepIdx], conditions: conds };
      return { ...p, steps };
    });
  }

  function removeCondition(stepIdx, condIdx) {
    setEditing(p => {
      const steps = [...p.steps];
      steps[stepIdx] = { ...steps[stepIdx], conditions: steps[stepIdx].conditions.filter((_, i) => i !== condIdx) };
      return { ...p, steps };
    });
  }

  // Drag and drop handlers
  function handleDragStart(idx) { setDragIdx(idx); }
  function handleDragOver(e, idx) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setEditing(p => {
      const steps = [...p.steps];
      const [moved] = steps.splice(dragIdx, 1);
      steps.splice(idx, 0, moved);
      return { ...p, steps };
    });
    setDragIdx(idx);
  }
  function handleDragEnd() { setDragIdx(null); }

  async function saveWorkflow() {
    if (!editing.name) { flash('Name is required', 'error'); return; }
    await wfApi('/workflows', { method: 'POST', body: JSON.stringify(editing) });
    flash('Workflow saved');
    setEditing(null);
    loadAll();
  }

  async function toggleActive(wf) {
    const endpoint = wf.status === 'Active' ? 'deactivate' : 'activate';
    await wfApi(`/workflows/${wf.id}/${endpoint}`, { method: 'POST' });
    flash(`Workflow ${endpoint}d`);
    loadAll();
  }

  async function deleteWorkflow(id) {
    await wfApi(`/workflows/${id}`, { method: 'DELETE' });
    flash('Workflow deleted');
    loadAll();
  }

  // ── Simulation functions ──
  async function startSimulation(wf) {
    let fDef = null;
    if (wf.form_id) {
      fDef = await wfApi(`/forms/${wf.form_id}`);
    }
    setSimWf(wf);
    setSimForm(fDef);
    setSimStep(-1); // start at form filling
    setSimFormValues({});
    setSimLog([]);
    setSimComment('');
    setSimForwardTo('');
  }

  function simSubmitForm() {
    setSimLog([{ action: 'initiated', actor: 'You (Initiator)', time: new Date().toLocaleTimeString(), comment: 'Workflow initiated' }]);
    setSimStep(0);
    setSimComment('');
  }

  function simAction(action) {
    const step = simWf.steps[simStep];
    const entry = {
      action,
      actor: `You as ${step?.recipient_name || step?.recipient_type || 'Recipient'} (Step ${simStep + 1})`,
      time: new Date().toLocaleTimeString(),
      comment: simComment || '',
    };
    if (action === 'forward') entry.comment = `Forwarded to: ${simForwardTo}. ${simComment}`;
    setSimLog(prev => [...prev, entry]);
    setSimComment('');
    setSimForwardTo('');

    if (action === 'approve') {
      if (simStep + 1 < simWf.steps.length) {
        setSimStep(simStep + 1);
      } else {
        setSimLog(prev => [...prev, { action: 'completed', actor: 'System', time: new Date().toLocaleTimeString(), comment: 'Workflow completed — all steps approved' }]);
        setSimStep(999);
      }
    } else if (action === 'reject') {
      setSimLog(prev => [...prev, { action: 'rejected', actor: 'System', time: new Date().toLocaleTimeString(), comment: 'Workflow rejected' }]);
      setSimStep(999);
    } else if (action === 'forward') {
      // Stay on same step, just log
    } else if (action === 'resend') {
      setSimLog(prev => [...prev, { action: 'resent', actor: 'System', time: new Date().toLocaleTimeString(), comment: 'Sent back to initiator' }]);
      setSimStep(-1);
    } else if (action === 'cancel') {
      setSimLog(prev => [...prev, { action: 'cancelled', actor: 'System', time: new Date().toLocaleTimeString(), comment: 'Workflow cancelled' }]);
      setSimStep(999);
    }
  }

  const simActionColors = { initiated: N.cyan, approve: N.teal, approved: N.teal, reject: N.coral, rejected: N.coral, forward: N.orange, resent: N.yellow, cancelled: '#64748B', completed: N.lime };

  // Get form fields for conditions
  const selectedForm = editing ? forms.find(f => f.id === editing.form_id) : null;
  const formFields = selectedForm?.fields || [];

  if (loading) return <div style={S.empty}><div className="spinner" style={{ margin: '0 auto 12px' }} />Loading...</div>;

  // ── Simulation view ──
  if (simWf) {
    const steps = simWf.steps || [];
    const isDone = simStep === 999;
    const isFormPhase = simStep === -1;
    const currentStep = !isFormPhase && !isDone ? steps[simStep] : null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={S.heading}>Simulate: {simWf.name}</div>
            <div style={S.muted}>Walk through each step as initiator and every recipient</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.ghost} onClick={() => startSimulation(simWf)}>{Icons.reply(N.cyan)} Restart</button>
            <button style={S.ghost} onClick={() => setSimWf(null)}>{Icons.x()} Exit Simulation</button>
          </div>
        </div>

        {/* Progress tracker */}
        <div style={{ ...S.card, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {/* Initiator node */}
            <div style={{
              width: 90, padding: '8px 0', textAlign: 'center', borderRadius: 10,
              background: isFormPhase ? `${N.cyan}18` : `${N.teal}10`,
              border: isFormPhase ? `2px solid ${N.cyan}` : `1px solid ${N.teal}40`,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: isFormPhase ? N.cyan : N.teal }}>INITIATOR</div>
              <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 2 }}>You</div>
            </div>
            {steps.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div style={{ flex: 1, height: 2, background: idx <= simStep ? N.teal : 'rgba(255,255,255,0.1)', minWidth: 20 }} />
                <div style={{
                  width: 100, padding: '8px 0', textAlign: 'center', borderRadius: 10,
                  background: idx === simStep ? `${N.violet}18` : idx < simStep ? `${N.teal}10` : 'rgba(255,255,255,0.02)',
                  border: idx === simStep ? `2px solid ${N.violet}` : idx < simStep ? `1px solid ${N.teal}40` : '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: idx === simStep ? N.violet : idx < simStep ? N.teal : '#64748B' }}>
                    STEP {idx + 1}
                  </div>
                  <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 2 }}>{step.recipient_name || step.recipient_type || step.name}</div>
                  {idx < simStep && <div style={{ fontSize: 8, color: N.teal, marginTop: 1 }}>✓ Approved</div>}
                </div>
              </React.Fragment>
            ))}
            <div style={{ flex: 1, height: 2, background: isDone && simLog[simLog.length - 1]?.action === 'completed' ? N.teal : 'rgba(255,255,255,0.1)', minWidth: 20 }} />
            <div style={{
              width: 80, padding: '8px 0', textAlign: 'center', borderRadius: 10,
              background: isDone ? (simLog[simLog.length - 1]?.action === 'completed' ? `${N.lime}15` : `${N.coral}15`) : 'rgba(255,255,255,0.02)',
              border: isDone ? `1px solid ${simLog[simLog.length - 1]?.action === 'completed' ? N.lime : N.coral}40` : '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: isDone ? (simLog[simLog.length - 1]?.action === 'completed' ? N.lime : N.coral) : '#64748B' }}>END</div>
            </div>
          </div>
        </div>

        {/* Current role panel */}
        <div style={{ ...S.card, borderLeft: `3px solid ${isFormPhase ? N.cyan : isDone ? N.lime : N.violet}` }}>
          {isFormPhase && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={S.badge(N.cyan)}>YOUR ROLE: Initiator</span>
                <span style={S.muted}>Fill in the form and submit to start the workflow</span>
              </div>
              {simForm && (simForm.fields || []).length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 16 }}>
                  {(simForm.fields || []).map((field, i) => {
                    const fName = field.name || field;
                    const fLabel = field.label || fName.replace(/_/g, ' ');
                    return (
                      <div key={i}>
                        <div style={S.label}>{fLabel}{field.required ? ' *' : ''}</div>
                        <input style={{ ...S.input, width: '100%' }}
                          value={simFormValues[fName] || ''}
                          placeholder={`Enter ${fLabel}`}
                          onChange={e => setSimFormValues(p => ({ ...p, [fName]: e.target.value }))} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ ...S.muted, marginBottom: 16 }}>No form linked — workflow starts without form data.</div>
              )}
              <button style={S.neonBtn(N.cyan)} onClick={simSubmitForm}>{Icons.play(N.cyan)} Submit & Start Workflow</button>
            </>
          )}

          {currentStep && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={S.badge(N.violet)}>YOUR ROLE: {currentStep.recipient_name || currentStep.recipient_type || 'Recipient'}</span>
                <span style={S.muted}>Step {simStep + 1}: {currentStep.name}</span>
              </div>
              {currentStep.custom_text && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                  background: 'rgba(191,95,255,0.06)', border: '1px solid rgba(191,95,255,0.15)',
                  fontSize: 12.5, color: '#CBD5E1', lineHeight: 1.6,
                }}>{currentStep.custom_text}</div>
              )}
              {/* Show form data summary */}
              {Object.keys(simFormValues).some(k => simFormValues[k]) && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ ...S.label, marginBottom: 6 }}>Form Data Submitted</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
                    {Object.entries(simFormValues).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', gap: 8 }}>
                        <span style={{ fontSize: 11.5, color: '#94A3B8', fontWeight: 600, minWidth: 110 }}>{k.replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: 11.5, color: '#E2E8F0' }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <textarea style={{ ...S.input, minHeight: 50, marginBottom: 12 }} value={simComment}
                placeholder="Add your comments as this recipient..."
                onChange={e => setSimComment(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={S.neonBtn(N.teal)} onClick={() => simAction('approve')}>{Icons.check(N.teal)} Approve</button>
                <button style={S.neonBtn(N.coral)} onClick={() => simAction('reject')}>{Icons.x(N.coral)} Reject</button>
                <button style={S.neonBtn(N.orange)} onClick={() => {
                  const name = simForwardTo || prompt('Forward to (name):');
                  if (name) { setSimForwardTo(name); simAction('forward'); }
                }}>{Icons.forward(N.orange)} Forward</button>
                <button style={S.neonBtn(N.yellow)} onClick={() => simAction('resend')}>{Icons.reply(N.yellow)} Resend</button>
                <button style={S.neonBtn('#94A3B8')} onClick={() => simAction('cancel')}>{Icons.pause('#94A3B8')} Cancel</button>
              </div>
            </>
          )}

          {isDone && (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>
                {simLog[simLog.length - 1]?.action === 'completed' ? Icons.check(N.lime) : Icons.x(N.coral)}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: simLog[simLog.length - 1]?.action === 'completed' ? N.lime : N.coral }}>
                Simulation {simLog[simLog.length - 1]?.action === 'completed' ? 'Completed Successfully' : 'Ended'}
              </div>
              <div style={{ ...S.muted, marginTop: 4 }}>
                {simLog[simLog.length - 1]?.comment}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
                <button style={S.neonBtn(N.cyan)} onClick={() => startSimulation(simWf)}>{Icons.reply(N.cyan)} Run Again</button>
                <button style={S.ghost} onClick={() => setSimWf(null)}>{Icons.x()} Exit</button>
              </div>
            </div>
          )}
        </div>

        {/* Action log */}
        {simLog.length > 0 && (
          <div style={S.card}>
            <div style={{ ...S.heading, marginBottom: 10 }}>Simulation Log</div>
            {simLog.map((entry, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ ...S.badge(simActionColors[entry.action] || '#94A3B8'), minWidth: 75, textAlign: 'center' }}>{entry.action}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, color: '#E2E8F0' }}>{entry.actor}</div>
                  {entry.comment && <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 2 }}>{entry.comment}</div>}
                </div>
                <span style={{ fontSize: 11, color: '#64748B' }}>{entry.time}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Editing view ──
  if (editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={S.heading}>{editing.id ? 'Edit Workflow' : 'New Workflow'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* View mode toggle */}
            <div style={{ display: 'flex', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
              <button onClick={() => setViewMode('sequential')} style={{
                padding: '6px 14px', fontSize: 12, border: 'none', cursor: 'pointer',
                background: viewMode === 'sequential' ? `${N.violet}18` : 'transparent',
                color: viewMode === 'sequential' ? N.violet : '#64748B',
              }}>Sequential</button>
              <button onClick={() => setViewMode('graph')} style={{
                padding: '6px 14px', fontSize: 12, border: 'none', cursor: 'pointer',
                background: viewMode === 'graph' ? `${N.violet}18` : 'transparent',
                color: viewMode === 'graph' ? N.violet : '#64748B',
              }}>Graph</button>
            </div>
            <button style={S.ghost} onClick={() => setEditing(null)}>{Icons.x()} Cancel</button>
            <button style={S.neonBtn(N.violet)} onClick={saveWorkflow}>{Icons.save(N.violet)} Save</button>
          </div>
        </div>

        {/* Workflow meta */}
        <div style={{ ...S.card, display: 'flex', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Workflow Name *</label>
            <input style={S.input} value={editing.name} placeholder="e.g. Leave Approval"
              onChange={e => setEditing(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Linked Form</label>
            <select style={{ ...S.select, width: '100%' }} value={editing.form_id}
              onChange={e => setEditing(p => ({ ...p, form_id: e.target.value }))}>
              <option value="">-- No form --</option>
              {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div style={{ width: 180 }}>
            <label style={S.label}>Trigger</label>
            <select style={{ ...S.select, width: '100%' }} value={editing.trigger_type}
              onChange={e => setEditing(p => ({ ...p, trigger_type: e.target.value }))}>
              <option value="manual">Manual</option>
              <option value="event">Event-Driven</option>
              <option value="schedule">Scheduled</option>
            </select>
          </div>
        </div>

        {/* Flex field label overrides for this workflow */}
        {editing.form_id && (() => {
          const linkedForm = forms.find(f => f.id === editing.form_id);
          const flexFields = (linkedForm?.fields || []).filter(f => f.flex);
          if (flexFields.length === 0) return null;
          const overrides = editing._flexOverrides || {};
          return (
            <div style={S.card}>
              <div style={{ ...S.heading, marginBottom: 8 }}>
                Flex Field Label Overrides
                <span style={{ fontSize: 11, color: '#64748B', fontWeight: 400, marginLeft: 8 }}>
                  (change labels for this workflow only)
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {flexFields.map(ff => (
                  <div key={ff.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize: 11, color: '#64748B', width: 70 }}>{ff.name.replace('flex_', '')}</span>
                    <input style={{ ...S.input, width: 150, padding: '4px 8px', fontSize: 11.5 }}
                      value={overrides[ff.name] ?? ff.label} placeholder={ff.label}
                      onChange={e => setEditing(p => ({
                        ...p,
                        _flexOverrides: { ...(p._flexOverrides || {}), [ff.name]: e.target.value }
                      }))} />
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Steps — Sequential view */}
        {viewMode === 'sequential' && (
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={S.heading}>Workflow Steps</div>
              <button style={S.neonBtn(N.violet)} onClick={addStep}>{Icons.plus(N.violet)} Add Step</button>
            </div>

            {editing.steps.length === 0 && <div style={S.muted}>No steps yet. Add a step to define the workflow flow.</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {editing.steps.map((step, idx) => (
                <div key={step.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  style={{
                    ...S.stepCard,
                    borderLeft: `3px solid ${N.violet}`,
                    opacity: dragIdx === idx ? 0.5 : 1,
                  }}>
                  {/* Step header */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ cursor: 'grab', color: '#475569' }}>{Icons.grip('#475569')}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: N.violet, background: `${N.violet}15`, padding: '2px 8px', borderRadius: 6 }}>
                      Step {idx + 1}
                    </span>
                    <input style={{ ...S.input, flex: 1 }} value={step.name} placeholder="Step name"
                      onChange={e => updateStep(idx, 'name', e.target.value)} />
                    <select style={{ ...S.select, width: 130 }} value={step.type} onChange={e => updateStep(idx, 'type', e.target.value)}>
                      <option value="approval">Approval</option>
                      <option value="review">Review</option>
                      <option value="notification">Notify</option>
                      <option value="condition">Condition</option>
                    </select>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F87171', padding: 4 }}
                      onClick={() => removeStep(idx)}>{Icons.trash('#F87171')}</button>
                  </div>

                  {/* Recipient */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, paddingLeft: 28 }}>
                    <label style={{ ...S.label, marginBottom: 0, width: 60 }}>Send to</label>
                    <select style={{ ...S.select, width: 140 }} value={step.recipient_type || 'user'}
                      onChange={e => updateStep(idx, 'recipient_type', e.target.value)}>
                      <option value="user">User</option>
                      <option value="role">Role</option>
                      <option value="manager">Direct Manager</option>
                      <option value="dotted_manager">Dotted Line Mgr</option>
                      <option value="initiator">Initiator</option>
                      <option value="group_hr">Group Tag → HR</option>
                      <option value="group_admin">Group Tag → Admin</option>
                      <option value="group_accounts">Group Tag → Accounts</option>
                      <option value="group_buddy">Group Tag → Buddy</option>
                    </select>
                    {(step.recipient_type === 'user' || step.recipient_type === 'role') && (
                      <input style={{ ...S.input, flex: 1 }} value={step.recipient_name || ''} placeholder={step.recipient_type === 'user' ? 'User name or email' : 'Role name'}
                        onChange={e => updateStep(idx, 'recipient_name', e.target.value)} />
                    )}
                    {step.recipient_type?.startsWith('group_') && (
                      <span style={{ fontSize: 11, color: '#64748B', fontStyle: 'italic' }}>
                        Resolved from initiator's group tag at runtime
                      </span>
                    )}
                  </div>

                  {/* Custom text */}
                  <div style={{ paddingLeft: 28, marginBottom: 8 }}>
                    <input style={{ ...S.input }} value={step.custom_text || ''} placeholder="Custom message for recipient (optional)"
                      onChange={e => updateStep(idx, 'custom_text', e.target.value)} />
                  </div>

                  {/* Conditions */}
                  <div style={{ paddingLeft: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>Conditions</span>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: N.cyan, fontSize: 11, fontWeight: 600 }}
                        onClick={() => addCondition(idx)}>+ Add</button>
                    </div>
                    {(step.conditions || []).map((cond, cIdx) => (
                      <div key={cond.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                        {cIdx > 0 && (
                          <select style={{ ...S.select, width: 60, fontSize: 11 }} value={cond.logic || 'AND'}
                            onChange={e => updateCondition(idx, cIdx, 'logic', e.target.value)}>
                            <option>AND</option><option>OR</option>
                          </select>
                        )}
                        <select style={{ ...S.select, flex: 1 }} value={cond.field}
                          onChange={e => updateCondition(idx, cIdx, 'field', e.target.value)}>
                          <option value="">-- Field --</option>
                          {formFields.map(f => <option key={f.name} value={f.name}>{f.label || f.name}</option>)}
                        </select>
                        <select style={{ ...S.select, width: 80 }} value={cond.operator}
                          onChange={e => updateCondition(idx, cIdx, 'operator', e.target.value)}>
                          <option value="==">= equals</option>
                          <option value="!=">≠ not</option>
                          <option value=">">{'>'} greater</option>
                          <option value="<">{'<'} less</option>
                          <option value=">=">≥ gte</option>
                          <option value="<=">≤ lte</option>
                          <option value="contains">contains</option>
                        </select>
                        <input style={{ ...S.input, width: 120 }} value={cond.value} placeholder="Value"
                          onChange={e => updateCondition(idx, cIdx, 'value', e.target.value)} />
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F87171', padding: 2 }}
                          onClick={() => removeCondition(idx, cIdx)}>{Icons.x('#F87171')}</button>
                      </div>
                    ))}
                  </div>

                  {/* Connector arrow */}
                  {idx < editing.steps.length - 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10, color: '#475569' }}>
                      {Icons.arrowRight('#475569')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Graph view */}
        {viewMode === 'graph' && (
          <div style={{ ...S.card, minHeight: 400, position: 'relative' }}>
            <div style={S.heading}>Visual Flow</div>
            <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, flexWrap: 'wrap', padding: 20 }}>
              {/* Start node */}
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: `${N.teal}15`, border: `2px solid ${N.teal}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: N.teal }}>START</div>
              </div>
              {editing.steps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <div style={{ color: '#475569', display: 'flex', alignItems: 'center' }}>
                    <svg width="40" height="2"><line x1="0" y1="1" x2="40" y2="1" stroke="#475569" strokeWidth="2" strokeDasharray="4,4"/></svg>
                    {Icons.arrowRight('#475569')}
                  </div>
                  <div style={{
                    width: 140, padding: 14, borderRadius: 12, textAlign: 'center',
                    background: `${N.violet}08`, border: `2px solid ${N.violet}40`,
                    boxShadow: `0 0 12px ${N.violet}10`,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: N.violet, marginBottom: 4 }}>STEP {idx + 1}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0' }}>{step.name}</div>
                    <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{step.type} → {step.recipient_name || step.recipient_type}</div>
                    {step.conditions?.length > 0 && (
                      <div style={{ fontSize: 9, color: N.cyan, marginTop: 4 }}>⚡ {step.conditions.length} condition(s)</div>
                    )}
                  </div>
                </React.Fragment>
              ))}
              <div style={{ color: '#475569', display: 'flex', alignItems: 'center' }}>
                <svg width="40" height="2"><line x1="0" y1="1" x2="40" y2="1" stroke="#475569" strokeWidth="2" strokeDasharray="4,4"/></svg>
                {Icons.arrowRight('#475569')}
              </div>
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: `${N.coral}15`, border: `2px solid ${N.coral}50`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: N.coral }}>END</div>
              </div>
            </div>
            <div style={{ textAlign: 'center', fontSize: 11, color: '#64748B', marginTop: 8 }}>
              Switch to Sequential view to edit steps. Graph view is a visual preview.
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List view ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={S.heading}>Workflows</div>
        <button style={S.neonBtn(N.violet)} onClick={startNew}>{Icons.plus(N.violet)} New Workflow</button>
      </div>

      {workflows.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{Icons.workflow('#475569')}</div>
          No workflows yet. Create a form first, then design a workflow.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {workflows.map(wf => (
            <div key={wf.id} style={{ ...S.cardSm, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${N.violet}12` }}>
                {Icons.workflow(N.violet)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: '#E2E8F0' }}>{wf.name}</div>
                <div style={{ fontSize: 11.5, color: '#64748B' }}>{wf.form_name || 'No form'} — {wf.steps?.length || 0} steps — {wf.trigger_type}</div>
              </div>
              <span style={S.badge(wf.status === 'Active' ? N.teal : wf.status === 'Draft' ? N.yellow : '#64748B')}>{wf.status}</span>
              {wf.steps?.length > 0 && (
                <button style={S.neonBtn(N.cyan)} onClick={() => startSimulation(wf)} title="Simulate workflow">{Icons.play(N.cyan)} Simulate</button>
              )}
              <button style={S.ghost} onClick={() => toggleActive(wf)} title={wf.status === 'Active' ? 'Deactivate' : 'Activate'}>
                {wf.status === 'Active' ? Icons.pause(N.orange) : Icons.play(N.teal)}
              </button>
              <button style={S.ghost} onClick={() => setEditing({ ...wf })}>{Icons.edit()}</button>
              <button style={{ ...S.ghost, color: '#F87171', borderColor: 'rgba(248,113,113,0.2)' }} onClick={() => deleteWorkflow(wf.id)}>{Icons.trash('#F87171')}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   INSTANCES TAB
   ═══════════════════════════════════════════════════════════ */
function InstancesTab({ flash }) {
  const [instances, setInstances] = useState([]);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  // New-request state
  const [showNew, setShowNew] = useState(false);
  const [activeWorkflows, setActiveWorkflows] = useState([]);
  const [chosenWf, setChosenWf] = useState(null);
  const [formDef, setFormDef] = useState(null);
  const [formValues, setFormValues] = useState({});

  const loadInstances = useCallback(async () => {
    const data = await wfApi('/instances');
    setInstances(data.instances || []);
  }, []);

  useEffect(() => { loadInstances().finally(() => setLoading(false)); }, []);

  // Load active workflows for "New Request"
  async function openNewRequest() {
    const data = await wfApi('/workflows');
    const active = (data.workflows || []).filter(w => w.status === 'Active');
    setActiveWorkflows(active);
    setChosenWf(null);
    setFormDef(null);
    setFormValues({});
    setShowNew(true);
  }

  // When user picks a workflow, load its linked form
  async function selectWorkflow(wf) {
    setChosenWf(wf);
    setFormValues({});
    if (wf.form_id) {
      const fData = await wfApi(`/forms/${wf.form_id}`);
      setFormDef(fData);
      // Pre-fill with empty values
      const vals = {};
      (fData.fields || []).forEach(f => { vals[f.name || f] = ''; });
      setFormValues(vals);
    } else {
      setFormDef(null);
    }
  }

  async function submitNewRequest() {
    if (!chosenWf) { flash('Select a workflow', 'error'); return; }
    await wfApi('/instances/start', {
      method: 'POST',
      body: JSON.stringify({ workflow_id: chosenWf.id, form_data: formValues }),
    });
    flash('Workflow started!');
    setShowNew(false);
    setChosenWf(null);
    setFormDef(null);
    setFormValues({});
    loadInstances();
  }

  async function viewInstance(inst) {
    const data = await wfApi(`/instances/${inst.id}`);
    setSelected(data);
  }

  async function doAction(action, extraBody = {}) {
    if (!selected) return;
    await wfApi(`/instances/${selected.id}/action`, {
      method: 'POST',
      body: JSON.stringify({ action, comments: comment, ...extraBody }),
    });
    flash(`Action: ${action}`);
    setComment('');
    setSelected(null);
    loadInstances();
  }

  const statusColors = { Pending: N.orange, Completed: N.teal, Rejected: N.coral, Cancelled: '#64748B', Resent: N.yellow };

  if (loading) return <div style={S.empty}><div className="spinner" style={{ margin: '0 auto 12px' }} />Loading...</div>;

  // ── New Request view ──
  if (showNew) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={S.heading}>New Request</div>
          <button style={S.ghost} onClick={() => setShowNew(false)}>{Icons.x()} Cancel</button>
        </div>

        {/* Step 1: Pick workflow */}
        <div style={S.card}>
          <div style={S.label}>Select Workflow *</div>
          {activeWorkflows.length === 0 ? (
            <div style={S.muted}>No active workflows. Activate a workflow in the Designer tab first.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              {activeWorkflows.map(wf => (
                <div key={wf.id}
                  onClick={() => selectWorkflow(wf)}
                  style={{
                    padding: '10px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                    border: chosenWf?.id === wf.id ? `1.5px solid ${N.cyan}` : '1px solid rgba(255,255,255,0.06)',
                    background: chosenWf?.id === wf.id ? `${N.cyan}10` : 'rgba(255,255,255,0.02)',
                  }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: chosenWf?.id === wf.id ? N.cyan : '#E2E8F0' }}>{wf.name}</div>
                  {wf.description && <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 2 }}>{wf.description}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Step 2: Fill form fields */}
        {chosenWf && formDef && (formDef.fields || []).length > 0 && (
          <div style={S.card}>
            <div style={{ ...S.heading, marginBottom: 10 }}>{formDef.name || 'Form'}</div>

            {/* Custom sections before fields */}
            {(formDef.custom_sections || []).filter(s => s.position !== 'after').map((sec, i) => (
              <div key={`sec-${i}`} style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                background: 'rgba(0,240,255,0.04)', border: '1px solid rgba(0,240,255,0.1)',
                fontSize: 12.5, color: '#94A3B8', lineHeight: 1.6
              }}>
                {sec.title && <div style={{ fontWeight: 700, color: '#E2E8F0', marginBottom: 4 }}>{sec.title}</div>}
                {sec.text}
              </div>
            ))}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
              {(formDef.fields || []).map((field, i) => {
                const fName = field.name || field;
                const fLabel = field.label || fName.replace(/_/g, ' ');
                const fRequired = field.required;
                const fType = field.type || 'text';
                const inputType = fType === 'date' ? 'date' : fType === 'time' ? 'time' : fType === 'number' ? 'number' : 'text';
                return (
                  <div key={i}>
                    <div style={S.label}>
                      {fLabel}{fRequired ? ' *' : ''}
                      {field.flex && <span style={{ fontSize: 9, color: N.cyan, marginLeft: 6 }}>FLEX</span>}
                    </div>
                    <input type={inputType} style={{ ...S.input, width: '100%' }}
                      value={formValues[fName] || ''}
                      placeholder={inputType === 'text' ? fLabel : ''}
                      onChange={e => setFormValues(p => ({ ...p, [fName]: e.target.value }))}
                    />
                  </div>
                );
              })}
            </div>

            {/* Custom sections after fields */}
            {(formDef.custom_sections || []).filter(s => s.position === 'after').map((sec, i) => (
              <div key={`sec-a-${i}`} style={{
                padding: '10px 14px', borderRadius: 8, marginTop: 12,
                background: 'rgba(0,240,255,0.04)', border: '1px solid rgba(0,240,255,0.1)',
                fontSize: 12.5, color: '#94A3B8', lineHeight: 1.6
              }}>
                {sec.title && <div style={{ fontWeight: 700, color: '#E2E8F0', marginBottom: 4 }}>{sec.title}</div>}
                {sec.text}
              </div>
            ))}
          </div>
        )}

        {chosenWf && !formDef && (
          <div style={{ ...S.card, ...S.muted }}>This workflow has no linked form. It will start without form data.</div>
        )}

        {/* Submit */}
        {chosenWf && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={S.ghost} onClick={() => setShowNew(false)}>{Icons.x()} Cancel</button>
            <button style={S.neonBtn(N.cyan)} onClick={submitNewRequest}>{Icons.play(N.cyan)} Start Workflow</button>
          </div>
        )}
      </div>
    );
  }

  // ── Detail view ──
  if (selected) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={S.heading}>{selected.metadata?.workflow_name || 'Workflow Instance'}</div>
          <button style={S.ghost} onClick={() => setSelected(null)}>{Icons.x()} Close</button>
        </div>

        {/* Status bar */}
        <div style={{ ...S.card, display: 'flex', gap: 20, alignItems: 'center' }}>
          <div>
            <div style={S.label}>Status</div>
            <span style={S.badge(statusColors[selected.status] || '#94A3B8')}>{selected.status}</span>
          </div>
          <div>
            <div style={S.label}>Current Step</div>
            <div style={{ fontSize: 13, color: '#E2E8F0' }}>{selected.metadata?.current_step_name || '—'}</div>
          </div>
          <div>
            <div style={S.label}>Recipient</div>
            <div style={{ fontSize: 13, color: '#E2E8F0' }}>{selected.metadata?.current_recipient_name || '—'}</div>
          </div>
          <div>
            <div style={S.label}>Initiated</div>
            <div style={{ fontSize: 13, color: '#E2E8F0' }}>{selected.initiated_at ? new Date(selected.initiated_at).toLocaleString() : '—'}</div>
          </div>
        </div>

        {/* Form data */}
        {Object.keys(selected.form_data || {}).length > 0 && (
          <div style={S.card}>
            <div style={{ ...S.heading, marginBottom: 10 }}>Form Data</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
              {Object.entries(selected.form_data).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, minWidth: 120 }}>{k.replace(/_/g, ' ')}</span>
                  <span style={{ fontSize: 12, color: '#E2E8F0' }}>{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action history */}
        <div style={S.card}>
          <div style={{ ...S.heading, marginBottom: 10 }}>Action History</div>
          {(selected.actions || []).map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ ...S.badge(statusColors[a.action === 'initiated' ? 'Pending' : a.action === 'approve' ? 'Completed' : a.action] || '#94A3B8'), minWidth: 70, textAlign: 'center' }}>{a.action}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, color: '#E2E8F0' }}>{a.actor_name || 'System'}</div>
                {a.comments && <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 2 }}>{a.comments}</div>}
              </div>
              <span style={{ fontSize: 11, color: '#64748B' }}>{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
            </div>
          ))}
        </div>

        {/* Actions panel */}
        {selected.status === 'Pending' && (
          <div style={S.card}>
            <div style={{ ...S.heading, marginBottom: 10 }}>Take Action</div>
            <textarea style={{ ...S.input, minHeight: 60, marginBottom: 12 }} value={comment}
              placeholder="Add your comments..."
              onChange={e => setComment(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={S.neonBtn(N.teal)} onClick={() => doAction('approve')}>{Icons.check(N.teal)} Approve</button>
              <button style={S.neonBtn(N.coral)} onClick={() => doAction('reject')}>{Icons.x(N.coral)} Reject</button>
              <button style={S.neonBtn(N.orange)} onClick={() => doAction('forward')}>{Icons.forward(N.orange)} Forward</button>
              <button style={S.neonBtn(N.yellow)} onClick={() => doAction('resend')}>{Icons.reply(N.yellow)} Resend</button>
              <button style={S.neonBtn('#94A3B8')} onClick={() => doAction('cancel')}>{Icons.pause('#94A3B8')} Cancel</button>
              <button style={{ ...S.ghost, color: '#F87171', borderColor: 'rgba(248,113,113,0.2)' }}
                onClick={() => doAction('delete')}>{Icons.trash('#F87171')} Delete</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List view ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={S.heading}>Workflow Instances</div>
        <button style={S.neonBtn(N.cyan)} onClick={openNewRequest}>{Icons.plus(N.cyan)} New Request</button>
      </div>

      {instances.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{Icons.instances('#475569')}</div>
          No workflow instances yet. Start a workflow from the Cockpit.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {instances.map(inst => (
            <div key={inst.id} style={{ ...S.cardSm, display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}
              onClick={() => viewInstance(inst)}>
              <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${statusColors[inst.status] || '#94A3B8'}12` }}>
                {Icons.instances(statusColors[inst.status] || '#94A3B8')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: '#E2E8F0' }}>{inst.workflow_name || 'Workflow'}</div>
                <div style={{ fontSize: 11.5, color: '#64748B' }}>
                  {inst.metadata?.current_step_name || '—'} → {inst.metadata?.current_recipient_name || '—'}
                </div>
              </div>
              <span style={S.badge(statusColors[inst.status] || '#94A3B8')}>{inst.status}</span>
              <span style={{ fontSize: 11, color: '#64748B' }}>{inst.initiated_at ? new Date(inst.initiated_at).toLocaleDateString() : ''}</span>
              <button style={S.ghost} onClick={e => { e.stopPropagation(); viewInstance(inst); }}>{Icons.eye()}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
